// src/components/CapoOfficina/CapoOfficinaWorkerRequests.jsx - Gestione richieste per meccanici
import React, { useState, useEffect } from 'react';
import { auth, getUserLeaveRequests, getAllUsers } from '../../firebase';
import CapoOfficinaRequestForm from './CapoOfficinaRequestForm';
import './CapoOfficinaWorkerRequests.css';
import Notification from '../Notification';
import './capoofficina.css';

const CapoOfficinaWorkerRequests = () => {
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success'
  });
  
  // Stati per l'autocompletamento
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedUserData, setSelectedUserData] = useState(null);

  // Carica meccanici e il capo officina stesso
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const allUsers = await getAllUsers();
        
        // Filtra solo meccanici + capo officina corrente
        const mechanics = allUsers.filter(user => 
          user.incarico === 'Meccanico' || user.id === auth.currentUser?.uid
        ).sort((a, b) => {
          const aName = a.nome && a.cognome ? `${a.nome} ${a.cognome}` : a.email;
          const bName = b.nome && b.cognome ? `${b.nome} ${b.cognome}` : b.email;
          return aName.localeCompare(bName);
        });
        
        setUsers(mechanics);
        console.log(`CapoOfficinaWorkerRequests: Caricati ${mechanics.length} utenti (meccanici + capo officina)`);
      } catch (error) {
        console.error("CapoOfficinaWorkerRequests: Errore nel caricamento utenti:", error);
        setError("Errore nel caricamento degli utenti");
      }
    };
    loadUsers();
  }, []);

  // Filtra gli utenti in base al termine di ricerca
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers([]);
      setShowSuggestions(false);
      return;
    }

    const filtered = users.filter(user => {
      const fullName = user.nome && user.cognome 
        ? `${user.nome} ${user.cognome}`.toLowerCase()
        : user.email.toLowerCase();
      
      return fullName.includes(searchTerm.toLowerCase());
    });

    setFilteredUsers(filtered);
    setShowSuggestions(true);
  }, [searchTerm, users]);

  // Carica le richieste quando cambia l'utente selezionato
  useEffect(() => {
    const loadRequests = async () => {
      if (!selectedUser) {
        setRequests([]);
        setIsLoading(false);
        return;
      }
      
      console.log(`CapoOfficinaWorkerRequests: Caricamento richieste per userId=${selectedUser}`);
      setIsLoading(true);
      
      try {
        const userRequests = await getUserLeaveRequests(selectedUser);
        console.log("CapoOfficinaWorkerRequests: Richieste caricate", userRequests);
        setRequests(userRequests);
        setError(null);
      } catch (err) {
        console.error("CapoOfficinaWorkerRequests: Errore nel caricamento delle richieste:", err);
        setError("Impossibile caricare le richieste. Riprova più tardi.");
        setRequests([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadRequests();
  }, [selectedUser]);

  // Gestisce l'invio di una nuova richiesta
  const handleRequestSubmit = async (requestData) => {
    // Ricarica le richieste per l'utente selezionato
    try {
      const userRequests = await getUserLeaveRequests(selectedUser);
      setRequests(userRequests);
    } catch (error) {
      console.error("Errore nel ricaricare le richieste:", error);
    }
    
    // Mostra notifica di successo
    const selectedUserData = users.find(u => u.id === selectedUser);
    const userName = selectedUserData ? 
      (selectedUserData.nome && selectedUserData.cognome ? 
        `${selectedUserData.nome} ${selectedUserData.cognome}` : 
        selectedUserData.email) : 'utente';
        
    setNotification({
      show: true,
      message: `Richiesta creata con successo per ${userName}!`,
      type: 'success'
    });
    
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 4000);
  };

  // Gestisce la selezione di un meccanico dalla ricerca
  const handleUserSelect = (user) => {
    const displayName = user.nome && user.cognome 
      ? `${user.nome} ${user.cognome}`
      : user.email;
    
    setSearchTerm(displayName);
    setSelectedUser(user.id);
    setSelectedUserData(user);
    setShowSuggestions(false);
  };

  // Gestisce il cambio del termine di ricerca
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    if (value.trim() === '') {
      setSelectedUser('');
      setSelectedUserData(null);
    }
  };

  // Gestisce il blur del campo di ricerca
  const handleSearchBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  // Formatta la data
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT');
  };

  // Ottiene il nome dell'utente selezionato
  const getSelectedUserName = () => {
    if (!selectedUserData) return '';
    return selectedUserData.nome && selectedUserData.cognome
      ? `${selectedUserData.nome} ${selectedUserData.cognome}`
      : selectedUserData.email;
  };

  // Ottiene la classe CSS per lo stato
  const getStatusClass = (status) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'approved': return 'status-approved';
      case 'rejected': return 'status-rejected';
      default: return '';
    }
  };

  // Ottiene il nome dello stato
  const getStatusName = (status) => {
    switch (status) {
      case 'pending': return 'In attesa';
      case 'approved': return 'Approvata';
      case 'rejected': return 'Rifiutata';
      default: return status;
    }
  };

  return (
    <div className="capoofficina-worker-requests">
      <Notification 
        show={notification.show}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification(prev => ({ ...prev, show: false }))}
      />

      <div className="worker-requests-header">
        <h2>Richieste Permessi/Ferie per Meccanici</h2>
        <p className="subtitle">Gestisci le richieste per te stesso e per i meccanici del team</p>
      </div>

      {/* Sezione selezione meccanico con autocompletamento */}
      <div className="user-selection-section">
        <div className="form-group autocomplete-wrapper">
          <label htmlFor="user-search">🔍 Cerca Meccanico</label>
          <input
            id="user-search"
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={() => searchTerm && setShowSuggestions(true)}
            onBlur={handleSearchBlur}
            placeholder="Inizia a digitare nome, cognome o email..."
            className="user-search-input"
          />
          
          {showSuggestions && filteredUsers.length > 0 && (
            <div className="autocomplete-suggestions">
              {filteredUsers.map(user => {
                const displayName = user.nome && user.cognome 
                  ? `${user.nome} ${user.cognome}`
                  : user.email;
                const badge = user.id === auth.currentUser?.uid ? '(Tu)' : '';
                
                return (
                  <div
                    key={user.id}
                    className="suggestion-item"
                    onClick={() => handleUserSelect(user)}
                  >
                    <strong>{displayName}</strong> {badge}
                    {user.incarico && (
                      <small className="user-role"> - {user.incarico}</small>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {selectedUser && (
          <button 
            className="btn-create-request"
            onClick={() => setShowRequestForm(true)}
          >
            ✨ Nuova Richiesta per {getSelectedUserName()}
          </button>
        )}
      </div>

      {/* Tabella richieste */}
      <div className="requests-table-container">
        {!selectedUser ? (
          <div className="no-selection-message">
            <p>👆 Seleziona un meccanico per visualizzare le sue richieste</p>
          </div>
        ) : isLoading ? (
          <div className="loading-message">
            <p>Caricamento richieste in corso...</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <p>{error}</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="requests-table">
              <thead>
                <tr>
                  <th>Data Richiesta</th>
                  <th>Tipo</th>
                  <th>Periodo</th>
                  <th>Stato</th>
                  <th>Note Admin</th>
                </tr>
              </thead>
              <tbody>
                {requests.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="no-requests">
                      Nessuna richiesta trovata per questo utente
                    </td>
                  </tr>
                ) : (
                  requests.map(request => (
                    <tr key={request.id}>
                      <td>{formatDate(request.createdAt)}</td>
                      <td>
                        {request.type === 'permission' && (
                          <>
                            <div>Permesso</div>
                            {request.permissionType === 'hourly' && (
                              <small className="text-muted">
                                Orario: {request.timeFrom} - {request.timeTo}
                              </small>
                            )}
                            {request.permissionType === 'daily' && (
                              <small className="text-muted">Giornaliero</small>
                            )}
                            {request.permissionType === 'multi-day' && (
                              <small className="text-muted">
                                Multi-giorno ({request.workingDaysCount} giorni)
                              </small>
                            )}
                          </>
                        )}
                        {request.type === 'vacation' && <div>Ferie</div>}
                        {request.type === 'sickness' && (
                          <>
                            <div>Malattia</div>
                            <small className="text-muted">
                              Protocollo: {request.protocolCode}
                            </small>
                            {request.dateTo && (
                              <small className="text-muted d-block">
                                Periodo: {formatDate(request.dateFrom)} - {formatDate(request.dateTo)}
                              </small>
                            )}
                          </>
                        )}
                      </td>
                      <td>
                        {request.dateFrom && formatDate(request.dateFrom)}
                        {request.dateTo && ` - ${formatDate(request.dateTo)}`}
                      </td>
                      <td>
                        <span className={`status-badge ${getStatusClass(request.status)}`}>
                          {getStatusName(request.status)}
                        </span>
                        {request.status === 'approved' && request.syncInfo && (
                          <div className="sync-status-indicator">
                            {request.syncInfo.syncResult ? (
                              <small className="text-success">
                                ✅ Sincronizzato ({request.syncInfo.syncDetails?.totalDates || 0} date)
                              </small>
                            ) : (
                              <small className="text-warning">
                                ⚠️ Errore sincronizzazione
                              </small>
                            )}
                          </div>
                        )}
                      </td>
                      <td>{request.adminNotes || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Form per l'aggiunta di nuove richieste */}
      <CapoOfficinaRequestForm 
        isVisible={showRequestForm} 
        onClose={() => setShowRequestForm(false)}
        onSubmit={handleRequestSubmit}
        selectedUserId={selectedUser}
        selectedUserName={getSelectedUserName()}
      />
    </div>
  );
};

export default CapoOfficinaWorkerRequests;